import Modal from './Modal'
import './_Rubric.scss'
import { GraderRubric, NewRubricScore } from '../Navbar/Grader'
import ScoreBar from '../Grader/Scorebar'
import useLoadingDots from '../../hooks/loadingDots'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'
import { LuCheck, LuLoaderCircle, LuNotepadText } from 'react-icons/lu'
import useIsPremium from '../../hooks/useIsPremium'

const RubricModal = ({
	isOpen,
	onClose,
	loading,
	rubric,
	overallScore,
	newRubricScore,
}: {
	isOpen: boolean
	onClose: () => void
	loading: boolean
	rubric: GraderRubric | null
	overallScore?: number
	newRubricScore?: NewRubricScore
}) => {
	const isPremium = useIsPremium()
	const graderV3 = useFeatureFlagVariantKey(FEATURE_FLAGS.GRADER_V3)
	const loadingDots = useLoadingDots(loading)
	const divider = <div className="border-b border-gray-500" />

	const contentAndIdeasScores = [
		{
			title: 'Relevance',
			description:
				'The content score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.relevanceToTopic ?? 50,
		},
		{
			title: 'Depth of Analysis',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.depthOfAnalysis ?? 50,
		},
		{
			title: 'Evidence and Support',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.evidenceAndSupport ?? 50,
		},
		{
			title: 'Consistent Tone',
			description:
				'The content score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.consistencyOfTone ?? 50,
		},
	]

	const grammarAndMechanicsScores = [
		{
			title: 'Grammar',
			description:
				'The content score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.correctGrammar ?? 50,
		},
		{
			title: 'Spelling',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.correctSpelling ?? 50,
		},
		{
			title: 'Word Choice',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.goodWordChoice ?? 50,
		},
		{
			title: 'Paragraph Transitions',
			description:
				'The ideas score is based on the quality of the ideas and information in your document. You can increase it by adding more relevant information.',
			score: rubric?.qualityOfParagraphTransitions ?? 50,
		},
	]

	const categoryScores = newRubricScore
		? [
				{
					title: 'Content & Analysis',
					description: newRubricScore.contentAndAnalysis.explanation,
					score: newRubricScore.contentAndAnalysis.score, // Convert to 10-point scale
					outOf: 25,
				},
				{
					title: 'Organization & Structure',
					description: newRubricScore.organizationAndStructure.explanation,
					score: newRubricScore.organizationAndStructure.score,
					outOf: 25,
				},
				{
					title: 'Critical Thinking',
					description: newRubricScore.criticalThinking.explanation,
					score: newRubricScore.criticalThinking.score,
					outOf: 25,
				},
				{
					title: 'Language & Style',
					description: newRubricScore.languageAndStyle.explanation,
					score: Math.round(newRubricScore.languageAndStyle.score * 0.8),
					outOf: 20,
				},
				{
					title: 'Citations & References',
					description: newRubricScore.citationsAndReferences.explanation,
					score: Math.round(newRubricScore.citationsAndReferences.score * 0.2),
					outOf: 5,
				},
		  ]
		: []

	const ScoreBarLocal = ({ score }: { score: number }) => {
		return (
			<div className="flex gap-1 items-center">
				<ScoreBar score={score} />
				<div className="text-xs">{score}</div>
			</div>
		)
	}

	const calculateOverallScore = () => {
		if (newRubricScore && graderV3 === 'test') {
			const total = Object.values(categoryScores)
				.filter((value) => typeof value === 'object' && 'score' in value)
				.reduce((sum, category: any) => sum + category.score, 0)

			return total
		}
		return (
			overallScore ??
			Math.floor(
				(((rubric?.relevanceToTopic ?? 0) +
					(rubric?.depthOfAnalysis ?? 0) +
					(rubric?.evidenceAndSupport ?? 0) +
					(rubric?.consistencyOfTone ?? 0) +
					(rubric?.correctGrammar ?? 0) +
					(rubric?.correctSpelling ?? 0) +
					(rubric?.goodWordChoice ?? 0) +
					(rubric?.qualityOfParagraphTransitions ?? 0)) /
					8) *
					10
			)
		)
	}

	const overallScoreLocal = calculateOverallScore()

	if (graderV3 === 'test') {
		return (
			<Modal open={isOpen} closeModal={onClose} portalClassName="rubric large">
				{loading ? (
					<div className="flex justify-center h-48 items-center">
						{LuLoaderCircle({
							className: 'text-gray-700 text-2xl animate-spin',
							size: 40,
						})}
					</div>
				) : (
					<div className="flex flex-col gap-5 py-8 px-6">
						<div className="flex flex-wrap gap-2 items-center justify-between border-b border-gray-200 pb-4">
							<div className="flex gap-3 items-center">
								{LuNotepadText({
									className: 'text-link text-2xl shrink-0',
									size: 24,
								})}
								<div>
									<div className="flex gap-3">
										<div className="text-xl font-semibold">Essay Evaluation</div>
									</div>
								</div>
							</div>
							<div className="flex justify-center items-center text-sm font-semibold text-link bg-[#e3ecfa] px-2 py-1 rounded-full w-full sm:w-auto">
								{overallScoreLocal}/100 ({overallScoreLocal}%)
							</div>
						</div>
						<div className="p-4 border bg-gray-50 rounded-xl">
							<div className="text-sm font-semibold text-gray-900 mb-1">Overall Assessment</div>
							<div className="text-sm text-gray-800 font-medium">{newRubricScore?.constructiveComments}</div>
						</div>
						<div className="flex flex-col gap-4">
							<div className="font-semibold">Score Breakdown</div>
							<div className="grid grid-cols-12">
								<div className="col-span-3 px-2 pt-1 pb-3 border-b border-gray-200 text-sm font-semibold text-gray-700">
									Aspect
								</div>
								<div className="md:col-span-4 col-span-3 px-2 pt-1 pb-3 border-b border-gray-200 text-sm font-semibold text-gray-700 text-center md:text-left">
									Score
								</div>
								<div className="md:col-span-5 col-span-6 px-2 pt-1 pb-3 border-b border-gray-200 text-sm font-semibold text-gray-700">
									Feedback
								</div>
								{categoryScores.map((item, index) => (
									<>
										<div
											key={index}
											className="col-span-3 md:px-2 pt-1 pb-3 border-b border-gray-200 text-xs md:text-sm font-semibold flex items-center"
										>
											{item.title}
										</div>
										<div className="md:col-span-4 col-span-3 px-2 pt-1 pb-3 border-b border-gray-200 text-sm font-semibold text-gray-700 items-center flex gap-2">
											<div className={`h-2.5 rounded-lg w-28 bg-gray-100 hidden md:block`}>
												<div
													className={`rounded-lg ${
														(item.score / item.outOf) * 100 < 70
															? (item.score / item.outOf) * 100 < 50
																? 'bg-danger-default'
																: 'bg-warning-default'
															: 'bg-success-default'
													} h-full`}
													style={{ width: `${(item.score / item.outOf) * 100}%` }}
												/>
											</div>
											<div className="text-sm font-semibold text-center md:text-left w-full md:w-auto">
												{item.score}/{item.outOf}
											</div>
										</div>
										<div className="md:col-span-5 col-span-6 px-2 py-3 border-b border-gray-200 md:text-sm text-xs font-medium text-gray-700">
											{item.description}
										</div>
									</>
								))}
							</div>
						</div>
						{isPremium && (
							<div className="flex flex-col gap-4">
								<div className="flex gap-2 items-center">
									{LuCheck({
										className: 'text-link',
										size: 20,
									})}
									<div className="font-semibold">Key Improvements</div>
								</div>
								<div className="grid md:grid-cols-2 gap-2">
									{[
										newRubricScore?.suggestion1,
										newRubricScore?.suggestion2,
										newRubricScore?.suggestion3,
										newRubricScore?.suggestion4,
									].map((suggestion, index) => (
										<div
											key={index}
											className="col-span-1 border border-gray-300 p-2 rounded-lg flex flex-col gap-1 items-start"
										>
											<div className="text-xs font-semibold text-link bg-[#e3ecfa] rounded-lg py-0.5 px-2">
												{suggestion?.category}
											</div>
											<div className="text-sm">{suggestion?.suggestion}</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</Modal>
		)
	}

	return (
		<Modal open={isOpen} closeModal={onClose} portalClassName="rubric">
			{loading ? (
				<div className="flex justify-center h-48 items-center">
					<div className="text-2xl font-semibold text-gray-700">Grading essay{loadingDots}</div>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<div className="flex gap-3 justify-between mb-3 items-center">
						<div className="flex flex-col gap-1">
							<div className="text-md font-semibold">Overall score: {overallScoreLocal} out of 100.</div>
							<div className="text-sm text-gray-700">{rubric?.constructiveComments}</div>
						</div>
						<div className="rounded-full flex justify-between items-center border-2 border-primary border-dotted ">
							<div className="text-primary w-20 h-20 text-center flex items-center justify-center font-semibold relative overflow-hidden">
								<div className="colors absolute h-full w-full z-0 rounded-full opacity-80" />
								<span className="text-3xl z-10 text-white">{overallScoreLocal}</span>
								<span className="text-md pt-1 z-10 text-white">%</span>
							</div>
						</div>
					</div>
					<div className="text-xl font-semibold">Content & Ideas</div>
					{divider}
					{contentAndIdeasScores.map((item, index) => (
						<div key={index} className="flex flex-col gap-2">
							<div className="flex gap-3 justify-between items-center">
								<div className="text-sm font-semibold">{item.title}:</div>
								<ScoreBarLocal score={item.score} />
							</div>
						</div>
					))}
					<div className="flex flex-col gap-0.5">
						<div className="text-sm font-semibold">Thesis statement:</div>
						<div className="text-sm text-gray-700">{rubric?.thesisStatement}</div>
					</div>
					<div className="text-xl font-semibold">Grammar & Mechanics</div>
					{divider}
					{grammarAndMechanicsScores.map((item, index) => (
						<div key={index} className="flex flex-col gap-2">
							<div className="flex gap-3 justify-between items-center">
								<div className="text-sm font-semibold">{item.title}:</div>
								<ScoreBarLocal score={item.score} />
							</div>
						</div>
					))}
					<div className="flex flex-col gap-0.5">
						<div className="text-sm font-semibold">Dead words used:</div>
						<div className="text-sm text-gray-700">{rubric?.weakWords}</div>
					</div>
				</div>
			)}
		</Modal>
	)
}

export default RubricModal
